import { getSearchParams, UrlUtils } from '@wix/package-picker-shared';
import { useCallback } from 'react';
import { useCurrentModule } from '../../components';
import { useAnonymous } from '../useAnonymous';
const DEFAULT_CHECKOUT_URL = 'https://manage.wix.com/premium-pricing/wix/checkout';
const SITE_SELECTOR_URL = 'https://www.wix.com/my-account/site-selector/';
const SITE_GUID_PLACEHOLDER = '{{metaSiteId}}';
/**
 * Hook for handling anonymous user checkout flow.
 *
 * When an anonymous user attempts to checkout, they are redirected through:
 * Sign In → Site Selector → Checkout (with selected siteGuid)
 *
 * The site selector replaces {{metaSiteId}} placeholder with the actual site ID.
 */
export const useAnonymousCheckoutRedirect = options => {
  const {
    isAnonymous,
    buildSignInRedirectionLink
  } = useAnonymous();
  const module = useCurrentModule();
  const {
    t
  } = module.internals.useTranslations();

  /**
   * Redirects anonymous users to sign-in flow with site selector.
   * @returns `true` if redirect happened (user is anonymous), `false` otherwise
   */
  const redirectToCheckoutWithSignIn = useCallback(() => {
    if (!isAnonymous) {
      return false;
    }
    const searchParams = getSearchParams().allSearchParams;
    const checkoutUrl = (options == null ? void 0 : options.checkoutUrl) ?? DEFAULT_CHECKOUT_URL;

    // Build checkout URL with {{metaSiteId}} placeholder - site selector will replace it
    const checkoutActionUrl = UrlUtils.buildUrl({
      url: checkoutUrl,
      params: {
        ...Object.fromEntries(searchParams.entries()),
        siteGuid: SITE_GUID_PLACEHOLDER
      }
    });

    // Build site selector URL with checkout as the actionUrl
    const siteSelectorUrl = UrlUtils.buildUrl({
      url: SITE_SELECTOR_URL,
      params: {
        title: t('site_selector.title') || 'Select a Site',
        buttonText: t('site_selector.button_text') || 'Continue',
        actionUrl: checkoutActionUrl
      }
    });
    const signInUrl = buildSignInRedirectionLink({
      postSignUpUrl: siteSelectorUrl,
      postLoginUrl: siteSelectorUrl
    });
    window.open(signInUrl, '_self');
    return true;
  }, [isAnonymous, buildSignInRedirectionLink, t, options == null ? void 0 : options.checkoutUrl]);
  return {
    /** Whether the current user is anonymous */
    isAnonymous,
    /**
     * Redirects anonymous users to sign-in → site selector → checkout flow.
     * @returns `true` if redirect happened, `false` if user is not anonymous
     */
    redirectToCheckoutWithSignIn
  };
};
//# sourceMappingURL=useAnonymousCheckoutRedirect.js.map